import { createAsyncThunk } from '@reduxjs/toolkit';

import { driverAPI } from '@/modules/driver/api';
import { IDriver, TUpdateDriverForm } from '@/modules/driver/model/driver';
import { namespace } from '@/modules/driver/namespace';
import { getCurrentDriver } from '@/modules/driver/store/selectors';
import type { RootState } from '@/root-store';
import { ThunkResult } from '@/shared/actions/thunk-result';
import { ISubmitFormResult } from '@/shared/models/formSubmit';
import { getPhoneFromCodeAndNumber } from '@/shared/utils/phoneUtils';

import { updateDriver } from '../actions';

let lastRequestId: string | undefined;

const updateDriverInfo = createAsyncThunk<
  ThunkResult<ISubmitFormResult<TUpdateDriverForm>>,
  TUpdateDriverForm
>(
  `${namespace}/submit-update-driver-info`,
  async (formValues, { dispatch, requestId, getState }) => {
    try {
      if (lastRequestId !== undefined) {
        return { isAlreadyExecuted: true };
      }

      lastRequestId = requestId;

      const result: ISubmitFormResult<TUpdateDriverForm> = {};

      const driver = getCurrentDriver(getState() as RootState);

      if (!driver) {
        return {
          isSucceed: false,
        };
      }

      const updatedFiels: Partial<IDriver> = {};

      if ('carId' in formValues) {
        updatedFiels.carId = formValues.carId;
      }

      if ('phoneCode' in formValues && 'phoneNumber' in formValues) {
        updatedFiels.phone = getPhoneFromCodeAndNumber({
          code: formValues.phoneCode,
          number: formValues.phoneNumber,
        });
      }

      const [, driverUpdateErrors] = await driverAPI.updateDriver(driver.id, {
        ...driver,
        ...updatedFiels,
      });

      if (driverUpdateErrors) {
        const { phone: phoneError, ...other } = driverUpdateErrors;

        if (phoneError) {
          result.validationErrors = {
            phoneNumber: phoneError,
          };
        }

        result.baseErrors = Object.values(other).flat();
      }

      if (!driverUpdateErrors) {
        dispatch(updateDriver({ id: driver.id, update: updatedFiels }));
      }

      return {
        isSucceed: true,
        result,
      };
    } finally {
      lastRequestId = undefined;
    }
  },
);

export { updateDriverInfo };
